import { motion } from 'motion/react'
import { SEO } from '@/lib/seo'
import { Badge } from '@/components/ui'
import { companyInfo } from '@/data/company'

export function Cookies() {
  return (
    <>
      <SEO
        title="Cookie Policy"
        description="UC Advisors Cookie Policy - Learn about how we use cookies on our website and how to manage your preferences."
      />

      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <Badge className="mb-6">Legal</Badge>
            <h1 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-8">
              Cookie Policy
            </h1>

            <div className="prose prose-invert prose-slate max-w-none">
              <p className="text-slate-300 text-lg mb-8">
                Last updated: March 2026
              </p>

              <div className="space-y-8">
                {/* Introduction */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    What Are Cookies?
                  </h2>
                  <p className="text-slate-400 mb-4">
                    Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit a website. They are widely used to make websites work more efficiently, provide a better user experience, and give website owners information about how their site is being used.
                  </p>
                  <p className="text-slate-400">
                    This Cookie Policy explains what cookies we use, why we use them, and how you can control them.
                  </p>
                </section>

                {/* Types of Cookies */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    Types of Cookies We Use
                  </h2>
                  
                  <div className="space-y-6">
                    <div className="border-l-4 border-green-500 pl-4">
                      <h3 className="text-lg font-semibold text-white mb-2">Essential Cookies (Required)</h3>
                      <p className="text-slate-400 mb-2">
                        These cookies are necessary for the website to function properly. They cannot be disabled.
                      </p>
                      <ul className="list-disc list-inside text-slate-400 space-y-1 text-sm">
                        <li><code className="text-accent-400">uca_cookie_consent</code> - Stores your cookie preferences (1 year)</li>
                        <li><code className="text-accent-400">__cf_bm</code> - Cloudflare bot management (30 minutes)</li>
                      </ul>
                    </div>

                    <div className="border-l-4 border-blue-500 pl-4">
                      <h3 className="text-lg font-semibold text-white mb-2">Analytics Cookies (Optional)</h3>
                      <p className="text-slate-400 mb-2">
                        These cookies help us understand how visitors interact with our website by collecting anonymous information.
                      </p>
                      <ul className="list-disc list-inside text-slate-400 space-y-1 text-sm">
                        <li><code className="text-accent-400">_ga</code> - Google Analytics identifier (2 years)</li>
                        <li><code className="text-accent-400">_ga_*</code> - Google Analytics session data (2 years)</li>
                        <li><code className="text-accent-400">_gid</code> - Google Analytics daily identifier (24 hours)</li>
                      </ul>
                    </div>

                    <div className="border-l-4 border-purple-500 pl-4">
                      <h3 className="text-lg font-semibold text-white mb-2">Marketing Cookies (Optional)</h3>
                      <p className="text-slate-400 mb-2">
                        These cookies are used to track visitors across websites for advertising purposes and to measure marketing campaign effectiveness.
                      </p>
                      <ul className="list-disc list-inside text-slate-400 space-y-1 text-sm">
                        <li><code className="text-accent-400">hubspotutk</code> - HubSpot visitor tracking (13 months)</li>
                        <li><code className="text-accent-400">__hssc</code> - HubSpot session tracking (30 minutes)</li>
                        <li><code className="text-accent-400">__hssrc</code> - HubSpot session identification (session)</li>
                        <li><code className="text-accent-400">__hstc</code> - HubSpot visitor tracking (13 months)</li>
                      </ul>
                    </div>

                    <div className="border-l-4 border-orange-500 pl-4">
                      <h3 className="text-lg font-semibold text-white mb-2">Functional Cookies (Optional)</h3>
                      <p className="text-slate-400 mb-2">
                        These cookies enable enhanced functionality and personalization, such as remembering your preferences.
                      </p>
                      <ul className="list-disc list-inside text-slate-400 space-y-1 text-sm">
                        <li><code className="text-accent-400">messagesUtk</code> - HubSpot chat widget (13 months)</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Managing Cookies */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    How to Manage Your Cookie Preferences
                  </h2>
                  
                  <h3 className="text-lg font-semibold text-white mt-4 mb-3">Using Our Cookie Banner</h3>
                  <p className="text-slate-400 mb-4">
                    When you first visit our website, you'll see a cookie consent banner that allows you to:
                  </p>
                  <ul className="list-disc list-inside text-slate-400 space-y-2 mb-6">
                    <li><strong className="text-white">Accept All:</strong> Enable all cookies including analytics and marketing</li>
                    <li><strong className="text-white">Decline All:</strong> Only essential cookies will be used</li>
                    <li><strong className="text-white">Cookie Settings:</strong> Choose which categories to enable</li>
                  </ul>
                  <p className="text-slate-400 mb-6">
                    You can change your preferences at any time by clicking "Cookie Preferences" in the footer of our website.
                  </p>

                  <h3 className="text-lg font-semibold text-white mt-6 mb-3">Using Browser Settings</h3>
                  <p className="text-slate-400 mb-4">
                    Most web browsers allow you to control cookies through their settings. You can typically find these in the "Options," "Preferences," or "Settings" menu. Common options include:
                  </p>
                  <ul className="list-disc list-inside text-slate-400 space-y-2">
                    <li>Block all cookies</li>
                    <li>Block third-party cookies only</li>
                    <li>Delete cookies when you close your browser</li>
                    <li>Receive a warning before a cookie is set</li>
                  </ul>
                  <p className="text-slate-400 mt-4">
                    Note: Blocking all cookies may affect website functionality.
                  </p>
                </section>

                {/* Do Not Sell - CCPA */}
                <section className="glass-card p-8 rounded-2xl border-2 border-accent-500/30">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    Do Not Sell or Share My Personal Information (California Residents)
                  </h2>
                  <p className="text-slate-400 mb-4">
                    Under the California Consumer Privacy Act (CCPA), California residents have the right to opt out of the "sale" or "sharing" of their personal information. While UC Advisors does not sell personal information for monetary consideration, some of our cookie-based data sharing for advertising purposes may constitute "sharing" under CCPA.
                  </p>
                  
                  <h3 className="text-lg font-semibold text-white mt-6 mb-3">How to Opt Out</h3>
                  <ul className="list-disc list-inside text-slate-400 space-y-2 mb-4">
                    <li>Click <strong className="text-white">"Decline All"</strong> or disable Marketing cookies in our cookie consent banner</li>
                    <li>Enable <strong className="text-white">Global Privacy Control (GPC)</strong> in your browser - we honor GPC signals</li>
                    <li>Email <strong className="text-white">privacy@ucadvisors.com</strong> with subject "Do Not Share"</li>
                  </ul>
                  <p className="text-slate-400">
                    When you opt out, we will stop sharing your information with third parties for targeted advertising purposes. Essential cookies will continue to function to ensure website operation.
                  </p>
                </section>

                {/* Third-Party Cookies */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    Third-Party Cookies
                  </h2>
                  <p className="text-slate-400 mb-4">
                    Some cookies on our website are set by third-party services. These services have their own privacy policies:
                  </p>
                  <ul className="list-disc list-inside text-slate-400 space-y-2">
                    <li><strong className="text-white">Google Analytics:</strong> <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-accent-400 hover:underline">Privacy Policy</a></li>
                    <li><strong className="text-white">HubSpot:</strong> <a href="https://legal.hubspot.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-accent-400 hover:underline">Privacy Policy</a></li>
                    <li><strong className="text-white">Cloudflare:</strong> <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer" className="text-accent-400 hover:underline">Privacy Policy</a></li>
                  </ul>
                </section>

                {/* Updates */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    Updates to This Policy
                  </h2>
                  <p className="text-slate-400">
                    We may update this Cookie Policy from time to time to reflect changes in technology, legislation, or our data practices. We will post the updated policy on this page with a new "Last updated" date. We encourage you to review this policy periodically.
                  </p>
                </section>

                {/* Contact */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    Contact Us
                  </h2>
                  <p className="text-slate-400 mb-4">
                    If you have any questions about our Cookie Policy or how we use cookies, please contact us:
                  </p>
                  <div className="text-slate-300 space-y-1">
                    <p className="font-semibold">{companyInfo.name}</p>
                    <p>{companyInfo.locations[0].address}</p>
                    <p>{companyInfo.locations[0].city}, {companyInfo.locations[0].state}</p>
                    <p>Email: privacy@ucadvisors.com</p>
                    <p>Phone: {companyInfo.phone}</p>
                  </div>
                </section>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}
